import { useState } from 'react';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';

// T468 — add a board without a deploy.
//
// The adapter FAMILIES are code; which boards they query need not be. T246
// measured 42 companies the pipeline cannot see and several were a slug away
// — every one of them a code change and a deploy.
//
// Two steps on purpose. T246 was written after `greenhouse:bcg` nearly got
// registered on the strength of an HTTP 200: the board answered, and served
// "Test Job" rows from Tampa. A slug that responds is not proof it is the
// right employer, so the probe shows what it SAW — the companies and a few
// real titles — and he decides whether that is who he meant.
const FAMILIES = ['greenhouse', 'teamtailor', 'ashby', 'lever'];

export default function AddBoard({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [family, setFamily] = useState('greenhouse');
  const [slug, setSlug] = useState('');
  const [host, setHost] = useState('');
  const [probing, setProbing] = useState(false);
  const [result, setResult] = useState(null);

  const probe = async () => {
    setProbing(true);
    setResult(null);
    try {
      setResult(await api.post('/sources/probe', { family, slug: slug.trim(), host: host.trim() }));
    } catch (e) {
      toast(e?.body?.error || e?.message || 'Probe failed', { kind: 'error' });
    } finally {
      setProbing(false);
    }
  };

  const add = async () => {
    try {
      await api.post('/sources', {
        family,
        slug: slug.trim(),
        host: host.trim(),
        displayName: result?.companies?.[0] || slug.trim(),
        verifiedCount: result?.rawCount ?? 0,
      });
      toast(`${family}:${slug.trim()} added — it is scanned from the next run`, { kind: 'success' });
      setSlug('');
      setHost('');
      setResult(null);
      setOpen(false);
      onAdded?.();
    } catch (e) {
      toast(e?.body?.error || e?.message || 'Could not add board', { kind: 'error' });
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 transition hover:border-brand-600 hover:text-brand-400"
      >
        Add a board
      </button>
    );
  }

  const input = 'rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200';
  return (
    <section className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <select value={family} onChange={(e) => setFamily(e.target.value)} className={input} aria-label="Board family">
          {FAMILIES.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="company slug, as it appears in the board URL"
          className={`${input} min-w-[18rem] flex-1`}
          aria-label="Board slug"
        />
        {family === 'teamtailor' && (
          <input
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="custom career host (optional)"
            className={`${input} min-w-[14rem]`}
            aria-label="Custom host"
          />
        )}
        <button
          type="button"
          onClick={probe}
          disabled={!slug.trim() || probing}
          className="rounded border border-brand-700 px-2 py-1 text-xs text-brand-300 transition hover:bg-brand-600/10 disabled:opacity-40"
        >
          {probing ? 'Checking…' : 'Check it'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:text-slate-300">
          Cancel
        </button>
      </div>

      {result && (
        <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-950/40 p-2.5 text-xs">
          {result.ok ? (
            <>
              <p className="text-slate-300">
                <span className="font-mono text-brand-400">{result.name}</span> answered with{' '}
                <span className="text-slate-100">{result.rawCount}</span> postings
                {result.keptCount !== result.rawCount && <> ({result.keptCount} match your search)</>}.
              </p>
              {/* The whole point of the preview: is this the employer you meant? */}
              {result.companies?.length > 0 && (
                <p className="text-slate-400">
                  Posting as: <span className="text-slate-200">{result.companies.join(', ')}</span>
                </p>
              )}
              <ul className="space-y-0.5 text-slate-500">
                {(result.sample ?? []).map((j) => (
                  <li key={`${j.company}-${j.title}`}>· {j.title}</li>
                ))}
              </ul>
              <button
                type="button"
                onClick={add}
                className="rounded border border-emerald-700 px-2 py-1 text-xs text-emerald-300 transition hover:bg-emerald-900/30"
              >
                Yes, that&apos;s the one — add it
              </button>
            </>
          ) : (
            <p className="text-amber-400">
              Nothing came back from <span className="font-mono">{result.name}</span>
              {result.reason ? <> — {result.reason}</> : null}. Check the slug against the board&apos;s own URL.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
